import React from 'react'
import { Link } from 'react-router-dom'
import AuthLayout, { AUTH_ACCENT, authFieldClass, BackToLogin } from '../../../components/AuthLayout'
import { AlertIcon, EyeIcon, EyeOffIcon, MailIcon } from '../../../template/kartera/icons'
import { REGISTER_PATH, useLoginFormModel } from './login-form-model'

const AuthError = ({ message }: { message: string }) => (
    <div role="alert" className="flex gap-2.5 rounded-xl bg-k-danger-soft px-3.5 py-3 text-sm font-semibold text-k-danger">
        <AlertIcon className="shrink-0" />
        {message}
    </div>
)

const LoginForm = () => {
    const model = useLoginFormModel()

    return (
        <AuthLayout>
            {model.view === 'login' && (
                <>
                    <div className="flex flex-col gap-1.5">
                        <h2 className="m-0 text-[28px] font-extrabold tracking-[-0.03em]">Inicia sesión</h2>
                        <p className="m-0 text-[15px] text-k-muted">Entra con tu correo de la agencia.</p>
                    </div>
                    {model.authError && <AuthError message={model.authError} />}
                    <form className="flex flex-col gap-4" onSubmit={model.submitLogin} noValidate>
                        <label className="flex flex-col gap-1.5 text-sm font-bold">
                            Correo electrónico
                            <input
                                type="email"
                                autoComplete="username"
                                value={model.email}
                                aria-invalid={model.emailErr}
                                placeholder="nombre@agencia.com"
                                onChange={(event) => model.setEmail(event.target.value)}
                                className={authFieldClass(model.emailErr)}
                            />
                            {model.emailErr && <span className="text-[13px] font-medium text-k-danger">Escribe un correo válido.</span>}
                        </label>
                        <div className="flex flex-col gap-1.5">
                            <div className="flex items-baseline justify-between gap-2.5">
                                <label htmlFor="k-pass" className="text-sm font-bold">Contraseña</label>
                                <button type="button" onClick={model.goRecover} className="cursor-pointer border-0 bg-transparent p-1 text-[13px] font-bold text-k-accent-ink">
                                    ¿Olvidaste tu contraseña?
                                </button>
                            </div>
                            <span className={`flex h-[50px] items-center overflow-hidden rounded-xl bg-k-surface ${model.passErr ? 'border-2 border-k-danger' : 'border border-k-line-strong'}`}>
                                <input
                                    id="k-pass"
                                    type={model.showPassword ? 'text' : 'password'}
                                    autoComplete="current-password"
                                    value={model.password}
                                    aria-invalid={model.passErr}
                                    onChange={(event) => model.setPassword(event.target.value)}
                                    className="h-full min-w-0 flex-1 border-0 bg-transparent px-3.5 text-[15px] text-k-ink outline-none"
                                />
                                <button
                                    type="button"
                                    aria-label={model.showPassword ? 'Ocultar contraseña' : 'Mostrar contraseña'}
                                    aria-pressed={model.showPassword}
                                    onClick={model.toggleShowPassword}
                                    className="flex h-full w-11 shrink-0 cursor-pointer items-center justify-center border-0 bg-transparent text-k-muted"
                                >
                                    {model.showPassword ? <EyeOffIcon /> : <EyeIcon />}
                                </button>
                            </span>
                            {model.passErr && <span className="text-[13px] font-medium text-k-danger">Escribe tu contraseña.</span>}
                        </div>
                        <label className="flex min-h-10 items-center gap-2.5 text-sm text-k-ink-2">
                            <input
                                type="checkbox"
                                checked={model.remember}
                                onChange={model.toggleRemember}
                                className="m-0 h-[18px] w-[18px]"
                                style={{ accentColor: AUTH_ACCENT }}
                            />
                            Mantener la sesión iniciada en este equipo
                        </label>
                        <button
                            type="submit"
                            disabled={model.loading}
                            className="flex h-[52px] items-center justify-center gap-2.5 rounded-[14px] border-0 text-base font-extrabold text-white"
                            style={{ background: AUTH_ACCENT, opacity: model.loading ? 0.85 : 1, cursor: model.loading ? 'progress' : 'pointer' }}
                        >
                            {model.loading && <span aria-hidden="true" className="h-[18px] w-[18px] rounded-full border-2 border-white/40 border-t-white" style={{ animation: 'k-spin 0.8s linear infinite' }} />}
                            {model.loading ? 'Entrando…' : 'Iniciar sesión'}
                        </button>
                    </form>
                    <p className="m-0 border-t border-k-line pt-[18px] text-center text-sm text-k-muted">
                        ¿Aún no tienes cuenta?{' '}
                        <Link to={REGISTER_PATH} className="font-bold text-k-accent-ink no-underline">
                            Regístrate
                        </Link>
                    </p>
                </>
            )}

            {model.view === 'recuperar' && (
                <>
                    <BackToLogin onClick={model.goLogin} />
                    {!model.recSent ? (
                        <form className="flex flex-col gap-4" onSubmit={model.submitReset} noValidate>
                            <div className="flex flex-col gap-1.5">
                                <h2 className="m-0 text-[28px] font-extrabold tracking-[-0.03em]">Recupera tu contraseña</h2>
                                <p className="m-0 text-[15px] text-k-muted">Escribe el correo con el que entras y te enviaremos un enlace para crear una nueva.</p>
                            </div>
                            {model.authError && <AuthError message={model.authError} />}
                            <label className="flex flex-col gap-1.5 text-sm font-bold">
                                Correo electrónico
                                <input
                                    type="email"
                                    autoComplete="username"
                                    value={model.email}
                                    aria-invalid={model.emailErr}
                                    placeholder="nombre@agencia.com"
                                    onChange={(event) => model.setEmail(event.target.value)}
                                    className={authFieldClass(model.emailErr)}
                                />
                                {model.emailErr && <span className="text-[13px] font-medium text-k-danger">Escribe un correo válido.</span>}
                            </label>
                            <button
                                type="submit"
                                disabled={model.loading}
                                className="h-[52px] rounded-[14px] border-0 text-base font-extrabold text-white"
                                style={{ background: AUTH_ACCENT, cursor: model.loading ? 'progress' : 'pointer' }}
                            >
                                {model.loading ? 'Enviando…' : 'Enviar enlace'}
                            </button>
                        </form>
                    ) : (
                        <div role="status" className="flex flex-col items-start gap-3.5">
                            <span className="flex h-14 w-14 items-center justify-center rounded-[18px] bg-k-ok-soft text-k-ok">
                                <MailIcon />
                            </span>
                            <h2 className="m-0 text-[26px] font-extrabold tracking-[-0.03em]">Revisa tu correo</h2>
                            <p className="m-0 text-[15px] leading-[1.55] text-k-muted">
                                Si <strong className="text-k-ink">{model.email}</strong> tiene una cuenta, recibirás un enlace para restablecer tu contraseña.
                            </p>
                            <p className="m-0 text-sm text-k-muted">
                                ¿No te llegó? Revisa tu carpeta de spam o{' '}
                                <button
                                    type="button"
                                    onClick={model.resend}
                                    className="cursor-pointer border-0 bg-transparent p-0 text-sm font-bold text-k-accent-ink underline underline-offset-[3px]"
                                >
                                    {model.resent ? 'reenviado ✓' : 'reenvíalo'}
                                </button>
                            </p>
                        </div>
                    )}
                </>
            )}
        </AuthLayout>
    )
}

export default LoginForm
